import React, { useState, useEffect, useRef } from 'react';
import Accordion from '@vfuk/core-accordion';
import Paginator from '@vfuk/core-paginator';
import { OverlayProvider } from '@vfuk/core-overlay-controller';
import Modal from '@vfuk/core-modal';
import ShowError from '../../SectionAssessment/ShowError';
import {
  AccordionDataPropTypes,
  VoteObjectType,
} from '../Types/AccordionDataTypes';
import '../css/AccordionData.css';
import {
  KOA_API_ENDPOINT,
  OPCO_CODE,
  LANG_CODE,
} from '../../SectionAssessment/SectionAssessmentSurvey/SectionAssessmentBusinessGoals/SectionAssessmentBusinessGoals';
import twinxClient from '../ProtectedApiClient';
import {
  AccordionWrapper,
  PaginatorWrapper,
  ModalContainer,
  ModalText,
} from '../styles/AccordionData.styles';
import AccordionDataItem from './AccordionDataItem';
import fireButtonAndLinkClicksEvent from "../../../../../helpers/fireButtonAndLinkClicksEvent";

const scrollToRef = ref =>
  window.scrollTo({
    top: ref.current.offsetTop - 100,
    left: 0,
    behavior: 'smooth',
  });

const AccordionData = ({
  results,
  pagenationTotalPages,
  dropdown,
  getDocumentListAPI,
  questiontext,
  content,
  error,
  tagString,
  votesList,
}: AccordionDataPropTypes) => {
  const paginationMaxLimit = content.paginationSliceCount;
  const [showOverlay, setToggleOverlay] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [accordionDatahandler, setAccordionDataHandler] =
    useState<boolean>(false);
  const [itemId, setitemId] = useState<string>();
  const [stopSubmitLoader, setStopSubmitLoader] = useState<boolean>(false);
  const [activeAccordions, setActiveAcordionsList] = useState<string[]>([]);
  const [voteObjectState, setVoteObjectState] = useState<VoteObjectType>();
  const [downvoteSubmitError, setDownvoteSubmitError] =
    useState<boolean>(false);

  const searchTopRef = useRef<HTMLDivElement>(null);
  const executeScroll = () => scrollToRef(searchTopRef);

  const documentRegisterApi = async (docId: string) => {
    const query: any = {
      ID: docId,
      Opco: OPCO_CODE,
      Language: LANG_CODE,
    };
    const params = new URLSearchParams(query).toString();
    try {
      const { data } = await twinxClient.get(
        `${KOA_API_ENDPOINT}/document?${params}`
      );
      if (!data?.result?.status && !data?.result?.fault) {
        //Calls only the api and no response to return/show
      }
    } catch (error) {
      console.log('request failed');
    }
  };

  const patchDocumentApi = async (
    docId: string,
    voteType: string,
    feedbackText: string,
    voteObject: VoteObjectType | null
  ) => {
    const reqtype = 'vote';
    const query: any = {
      ID: docId,
      votetype: voteType,
      Opco: OPCO_CODE,
      Language: LANG_CODE,
    };
    if (voteType === 'downvote') {
      Object.assign(query, { downvotedesp: feedbackText });
    }

    const params = new URLSearchParams(query).toString();

    try {
      const { data } = await twinxClient.get(
        `${KOA_API_ENDPOINT}/patchdocument?${params}`
      );
      if (data?.status === 200 && !data?.result?.fault) {
        tagString
          ? getDocumentListAPI(questiontext, dropdown, reqtype, tagString)
          : getDocumentListAPI(questiontext, dropdown, reqtype);
        onVoteSuccess(voteType);
        voteObject !== null && updateTempVoteList(docId, voteObject);
      } else {
        apiErrorHandler(docId, voteType);
      }
    } catch (error) {
      console.log(error);
      !window.navigator.onLine
        ? alert('please connect your network')
        : apiErrorHandler(docId, voteType);
    }
  };

  const apiErrorHandler = (docId: string, voteType: string) => {
    if (voteType === 'upvote') {
      alertHandler(docId, 'requestFailed');
    } else if (voteType === 'downvote') {
      setDownvoteSubmitError(true);
      setTimeout(() => {
        setDownvoteSubmitError(false);
      }, 1000);
      setStopSubmitLoader(true);
    }
  };

  const onVoteSuccess = async (voteType: string) => {
    if (voteType === 'upvote') {
      setToggleOverlay(!showOverlay);
    }
  };

  const updateTempVoteList = async (
    docId: string,
    voteObject: VoteObjectType
  ) => {
    const findIdInVoteList = votesList.filter((obj: any) => {
      return obj.id === docId;
    });

    if (findIdInVoteList.length === 0) {
      votesList.splice(0, 0, voteObject);
    } else {
      const UpdateVoteID = findIdInVoteList.map((obj: any) => {
        if (obj.upvote === false) {
          return {
            ...obj,
            upvote: true,
            downvote: findIdInVoteList[0].downvote,
          };
        } else {
          return { ...obj, upvote: findIdInVoteList[0].upvote, downvote: true };
        }
      });
      // removing matched item from array
      const objWithIdIndex = votesList.findIndex(
        (obj: any) => obj.id === findIdInVoteList[0].id
      );
      await votesList.splice(objWithIdIndex, 1);
      // adding updated object to voteslist
      await votesList.push(UpdateVoteID[0]);
    }
  };

  const filterVote = (
    findIdInVoteList: [VoteObjectType],
    docId: string,
    voteType: string,
    feedbackText: string | '',
    voteObject: VoteObjectType
  ) => {
    let canVote = true;
    findIdInVoteList.forEach(filteredVote => {
      if (voteType === 'upvote') {
        if (filteredVote.upvote === false) {
          patchDocumentApi(docId, voteType, feedbackText, voteObject);
          canVote = true;
        } else {
          alertHandler(docId, 'voteLimit');
          canVote = false;
        }
      } else {
        if (filteredVote.downvote === true) {
          alertHandler(docId, 'voteLimit');
          canVote = false;
        }
      }
    });
    return canVote;
  };

  const voteHandler = async (
    docId: string,
    voteType: string,
    feedbackText: string | ''
  ) => {
    const voteObject = {
      id: docId,
      upvote: voteType === 'upvote' ? true : false,
      downvote: voteType === 'downvote' ? true : false,
    };
    setVoteObjectState(voteObject);

    const findIdInVoteList = votesList.filter(filteredVoteList => {
      return filteredVoteList.id === docId;
    });

    if (findIdInVoteList.length === 0) {
      if (voteType === 'upvote') {
        await patchDocumentApi(docId, voteType, feedbackText, voteObject);
      }
      return true;
    } else {
      return filterVote(
        findIdInVoteList,
        docId,
        voteType,
        feedbackText,
        voteObject
      );
    }
  };

  const alertHandler = (id: string, type: string) => {
    if (type === 'voteLimit') {
      const alertElement = document.getElementById(id + '-vote');
      alertElement.style.display = 'block';
      setTimeout(() => {
        alertElement.style.display = 'none';
      }, 1000);
    } else if (type === 'requestFailed') {
      const alertElement = document.getElementById(id + '-request');
      alertElement.style.display = 'block';
      setTimeout(() => {
        alertElement.style.display = 'none';
      }, 1000);
    }
  };

  const paginationData = async () => {
    await setAccordionDataHandler(true);
    setActiveAcordionsList([]);
    await setAccordionDataHandler(false);
  };

  const splitResults = () => {
    const result = results.reduce((resultArray: any, item, index) => {
      const chunkIndex = Math.floor(index / paginationMaxLimit);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);
    return result;
  };

  const changePage = newPage => {
    setCurrentPage(newPage);
    paginationData();
    executeScroll();
  };

  const accordionHandler = async (itemId: string) => {
    const activelist = activeAccordions.filter((obj: any) => {
      return obj === itemId;
    });
    if (activelist.length === 0) {
      const headingText = results.filter(item => item.id === itemId)[0].content[0].content[0].topic;
      fireButtonAndLinkClicksEvent({ event_label: headingText, event_action: 'link click', event_category: 'smart search' });
      activeAccordions.push(itemId);
      await documentRegisterApi(itemId);
    } else {
      const findIndexRemoveActiveItem = activeAccordions.indexOf(itemId);
      if (findIndexRemoveActiveItem > -1) {
        activeAccordions.splice(findIndexRemoveActiveItem, 1);
      }
      setActiveAcordionsList(activeAccordions);
    }
  };

  useEffect(() => {
    paginationData();
  }, []);

  const splitedResults = splitResults();

  return (
    <div ref={searchTopRef}>
      {/* accordian section started*/}
      {error ? (
        <ShowError
          errorContent={error.errorContent}
          errorType={error.errorType}
        />
      ) : (
        <div className="search-results-accordion-section">
          {accordionDatahandler ? (
            <div style={{ height: '672px' }}></div>
          ) : (
            <div>
              <AccordionWrapper>
                <Accordion appearance="primary" multi={true}>
                  {splitedResults[currentPage - 1].map(
                    (item: any, index: number) => (
                      <>
                        <AccordionDataItem
                          accordionHandler={accordionHandler}
                          content={content}
                          downvoteSubmitError={downvoteSubmitError}
                          index={index}
                          item={item}
                          itemId={itemId}
                          patchDocumentApi={patchDocumentApi}
                          setitemId={setitemId}
                          setStopSubmitLoader={setStopSubmitLoader}
                          stopSubmitLoader={stopSubmitLoader}
                          voteHandler={voteHandler}
                          voteObjectState={voteObjectState}
                        />
                        {/* Upvote popup */}
                        <OverlayProvider>
                          <Modal
                            srName="upvote-modal"
                            isOpen={showOverlay}
                            size={4}
                            animate
                            isClosable
                            onCloseCb={() => {
                              setToggleOverlay(!showOverlay);
                            }}
                          >
                            <ModalContainer>
                              <img
                                src={content.upvotePopup.upvoteIcon.file.url}
                                alt="Demo image"
                              />
                              <ModalText>
                                {content.upvotePopup.upvoteThankyouText}
                              </ModalText>
                            </ModalContainer>
                          </Modal>
                        </OverlayProvider>
                        {/* Upvote popup closed */}
                      </>
                    )
                  )}
                </Accordion>
              </AccordionWrapper>
              {/* accordian section closed*/}

              {/* pagenation open */}
              <PaginatorWrapper>
                {pagenationTotalPages !== 1 && (
                  <Paginator
                    id="accordion-paginator"
                    numberOfPages={pagenationTotalPages}
                    currentPage={currentPage}
                    onClick={changePage}
                  />
                )}
              </PaginatorWrapper>
              {/* pagenation closed*/}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccordionData;
